/**
 * O vocabulário que os editores do painel oferecem — tirado do que o motor e o
 * pipeline realmente usam, e não de listas escritas em cada tela.
 *
 * Cada editor tinha a sua: o de armas oferecia cinco dos doze tipos, o de
 * classes idem, e "eficaz contra" e "arquétipo" listavam valores que nenhuma
 * classe tem (armadura, voador, dragão, soldado) enquanto omitiam três que
 * existem. Um valor que o motor não conhece não quebra nada — simplesmente não
 * faz efeito, que é o pior tipo de erro de conteúdo.
 */

import {
  CONDITION_IDS, CONDITIONS, WEAPON_TYPES,
  type ConditionId, type TriangleGroup, type WeaponType,
} from '@oz/rules';

const WEAPON_LABELS: Readonly<Record<WeaponType, { readonly label: string; readonly icon: string }>> = {
  espada: { label: 'Espada', icon: '🗡️' },
  lanca: { label: 'Lança', icon: '🔱' },
  machado: { label: 'Machado', icon: '🪓' },
  malho: { label: 'Malho', icon: '🔨' },
  adaga: { label: 'Adaga', icon: '🔪' },
  garra: { label: 'Garra', icon: '🐾' },
  luva: { label: 'Luva', icon: '🥊' },
  arco: { label: 'Arco', icon: '🏹' },
  besta: { label: 'Besta', icon: '🎯' },
  cajado: { label: 'Cajado', icon: '🪄' },
  chicote: { label: 'Chicote', icon: '➰' },
  arma_de_fogo: { label: 'Arma de fogo', icon: '🔫' },
};

/** Todos os tipos de arma do motor, na ordem dele, com rótulo e ícone. */
export const WEAPON_TYPE_OPTIONS: readonly { readonly id: WeaponType; readonly label: string; readonly icon: string }[] =
  WEAPON_TYPES.map((id) => ({ id, ...WEAPON_LABELS[id] }));

export function weaponTypeLabel(type: string): string {
  return WEAPON_LABELS[type as WeaponType]?.label ?? type;
}

/**
 * Onde cada tipo entra no triângulo — o mesmo `TRIANGLE_GROUP` do pipeline
 * (ozimport/config.py): o malho briga junto do machado; o resto fica de fora.
 */
export function triangleGroupFor(type: WeaponType): TriangleGroup | null {
  switch (type) {
    case 'espada':
    case 'lanca':
    case 'machado':
      return type;
    case 'malho':
      return 'machado';
    default:
      return null;
  }
}

/**
 * Os arquétipos de classe: os oito do acervo original (config.py) mais o que a
 * importação de outros jogos deduz (generic.py). O motor lê o arquétipo em dois
 * lugares — `ladrao` abre baú sem chave, e "eficaz contra" compara com ele.
 */
export const ARCHETYPES: readonly { readonly id: string; readonly label: string }[] = [
  { id: 'mirmidao', label: 'Mirmidão (ágil, crítico)' },
  { id: 'cavaleiro', label: 'Cavaleiro (montado)' },
  { id: 'cavaleiro_magico', label: 'Cavaleiro mágico (híbrido)' },
  { id: 'infantaria', label: 'Infantaria' },
  { id: 'lutador', label: 'Lutador (PV e força)' },
  { id: 'arqueiro', label: 'Arqueiro (distância)' },
  { id: 'mago', label: 'Mago (magia)' },
  { id: 'clerigo', label: 'Clérigo (cura)' },
  { id: 'ladrao', label: 'Ladrão (abre baús sem chave)' },
];

/** A lista, mais o valor atual se ele não estiver nela — para nada sumir da tela. */
export function archetypeOptions(current?: string): readonly { readonly id: string; readonly label: string }[] {
  return current && !ARCHETYPES.some((a) => a.id === current)
    ? [...ARCHETYPES, { id: current, label: `${current} (fora do vocabulário)` }]
    : ARCHETYPES;
}

/**
 * As condições que o motor conhece, com o que cada uma faz.
 *
 * É a mesma tabela que a partida usa (`CONDITIONS` em @oz/rules): o que o
 * painel oferecer aqui tem efeito garantido em campo, e o que não estiver aqui
 * não existe para o motor.
 */
export const CONDITION_OPTIONS: readonly {
  readonly id: ConditionId;
  readonly label: string;
  readonly icon: number;
  readonly turns: number;
  readonly summary: string;
}[] = CONDITION_IDS.map((id) => ({
  id,
  label: CONDITIONS[id].label,
  icon: CONDITIONS[id].icon,
  turns: CONDITIONS[id].turns,
  summary: CONDITIONS[id].summary,
}));
